import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import Image from 'next/image';
import { BaseCard } from '../../BaseCard/BaseCard';
import { Callout } from '../../Callout/Callout';
import { LastPlayedBackground } from '../../LastPlayedBackground/LastPlayedBackground';

type BackgroundImage = {
  background: string;
  width?: number;
  height?: number;
};

type GameCardProps = {
  id: string;
  gameProvider: string;
  position: number;
  'data-testid'?: string;
  className?: string;
  logoImage: string;
  backgroundImage: BackgroundImage;
  gameName: string;
  href: string;
  jackpotLabelText?: string;
  playButtonText?: string;
  priority?: boolean;
  backgroundType?: 'image' | 'svg' | 'solid';
};

function GameCard(props: GameCardProps) {
  const {
    'data-testid': dataTestId,
    id,
    className,
    logoImage,
    backgroundImage: { background, width, height },
    jackpotLabelText,
    playButtonText,
    href,
    gameName,
    priority,
    gameProvider,
    position = 0,
    backgroundType = 'image',
  } = props;

  const isSvgBackground = backgroundType === 'svg';
  const containerClasses = twMerge('relative group', className);
  const logoImageClassName = isSvgBackground ? 'h-20 w-28 object-contain' : 'size-28 object-contain';
  const logoImageWidth = isSvgBackground ? 112 : 113;
  const logoImageHeight = isSvgBackground ? 80 : 113;
  const logoWrapperClassName = twMerge(
    'absolute inset-0 flex justify-center',
    jackpotLabelText ? (isSvgBackground ? 'items-start' : 'items-start pt-2') : 'items-center',
    isSvgBackground ? '' : 'backdrop-blur-[2px]'
  );

  return (
    <BaseCard.Root
      href={href}
      className={containerClasses}
      data-testid={dataTestId}
      data-track-container="Game"
      data-track-game-id={id}
      data-track-game-name={gameName}
      data-track-game-provider={gameProvider}
      data-track-game-position={position.toString()}
      data-track-game-callout-show={jackpotLabelText ? 'true' : 'false'}
      data-track-game-callout-value={jackpotLabelText}
    >
      {isSvgBackground ? <LastPlayedBackground position={position} data-testid="game-card-last-played-background" className="absolute inset-0 size-full" /> : null}
      {backgroundType === 'solid' ? <div className="w-full h-full bg-background" /> : null}
      {backgroundType === 'image' ? (
        <Image src={background} priority={priority} alt={gameName} width={width ?? 136} height={height ?? 182} className={`w-full h-full object-cover`} />
      ) : null}

      <div data-testid="game-card-logo-image-wrapper" className={logoWrapperClassName}>
        <Image data-testid="game-card-logo-image" src={logoImage} width={logoImageWidth} height={logoImageHeight} alt={`${gameName} logo`} className={logoImageClassName} />

        <BaseCard.Content className="z-10 my-auto absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {playButtonText && (
            <Button
              className={`mx-auto transition-opacity hidden md:opacity-0 md:flex md:group-hover:opacity-100`}
              data-track-name="Play"
              aria-label={`Play ${gameName}`}
              data-testid="game-card-play-button"
            >
              {playButtonText}
            </Button>
          )}
        </BaseCard.Content>

        <BaseCard.Footer className="z-10">
          {jackpotLabelText && <Callout className="mx-auto select-none absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap">{jackpotLabelText}</Callout>}
        </BaseCard.Footer>
      </div>
    </BaseCard.Root>
  );
}

export { GameCard, type GameCardProps };
